import React from "react";
import PriceTable from "Components/PriceTable";
import LayoutPrice from "Components/Layouts/LayoutPrice";
import BottomBar from "Components/BottomBar";
import AdvDetail from "Components/AdvDetail";
import {
  Box,
  Typography,
  Tooltip,
  IconButton,
} from "@mui/material";
import {
  AdminPanelSettings as AdminIcon,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
} from "@mui/icons-material";
import {
  AdvancedRealTimeChart,
  TickerTape,
} from "react-ts-tradingview-widgets";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "contexts/AuthContext";
import { RefreshIntervalProvider } from "../../contexts/RefreshIntervalContext";
import QrModal from "Components/QrModal";

const LS_KEY = (pair: string) => `chartOn_${pair}`;

const Chart = React.memo(({ candleMode, language, pair, display }: any) => {
  const [chartKey, setChartKey] = React.useState(0);
  const [isOn, setIsOn] = React.useState(() => {
    const saved = localStorage.getItem(LS_KEY(pair));
    return saved !== null ? saved === "true" : true;
  });

  React.useEffect(() => {
    setChartKey((prev) => prev + 1);
  }, [pair]);

  const handleToggle = () => {
    setIsOn((prev) => {
      const next = !prev;
      localStorage.setItem(LS_KEY(pair), next ? "true" : "false");
      return next;
    });
  };

  return (
    <Box
      className="h-60 relative"
      sx={{
        backgroundColor: "#171B26",
        border: "none",
      }}
    >
      {isOn ? (
        <Box className="absolute bottom-0 h-full w-full z-0">
          <AdvancedRealTimeChart
            key={chartKey}
            symbol={`${pair}`}
            theme="dark"
            allow_symbol_change={true}
            hide_legend
            hide_side_toolbar
            autosize
            interval={candleMode}
            withdateranges={false}
            save_image={false}
            timezone={language === "vi" ? "Asia/Bangkok" : "Etc/UTC"}
            locale="vi_VN"
          />
        </Box>
      ) : (
        <Box className="absolute bottom-0 h-full w-full z-0 flex items-center justify-center">
          <Typography variant="body2" color="text.secondary">
            📊 Chart hidden
          </Typography>
        </Box>
      )}
      <Box className="top-10 inset-2/3 z-10 absolute opacity-5 flex flex-col items-center">
        <Typography variant="h4" color="text.primary">
          SharkTools
        </Typography>
      </Box>
      <Box
        className="absolute w-fit h-10 rounded-xl left-2 bottom-9 flex justify-center items-center px-2 border cursor-pointer animate-bounce"
        sx={{
          backgroundColor: "success.darker",
          borderColor: "success.main",
          color: "success.main",
        }}
      >
        {display ? display : pair.replace("BINANCE:", "").replace("USDT", "")}
      </Box>
      {/* Individual toggle */}
      <Box
        className="absolute right-2 top-2 z-20"
        onClick={handleToggle}
        sx={{
          width: 28,
          height: 28,
          borderRadius: "50%",
          backgroundColor: isOn
            ? "rgba(52, 211, 153, 0.85)"
            : "rgba(71, 85, 105, 0.85)",
          color: isOn ? "#0f172a" : "#94a3b8",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          cursor: "pointer",
          opacity: 0.8,
          transition: "opacity 0.2s, background-color 0.2s",
          "&:hover": { opacity: 1 },
        }}
      >
        {isOn ? (
          <VisibilityIcon sx={{ fontSize: 16 }} />
        ) : (
          <VisibilityOffIcon sx={{ fontSize: 16 }} />
        )}
      </Box>
    </Box>
  );
});

const SliderCoin = React.memo(() => {
  return (
    <div style={{ position: "relative" }}>
      <TickerTape
        colorTheme="dark"
        symbols={[
          { proName: "BINANCE:BTCUSDT", title: "Bitcoin" },
          { proName: "USDVND", title: "Giá USD" },
          { proName: "USDTUSD", title: "Giá USDT/$" },
          { proName: "USDCUSDT", title: "USDC" },
          { proName: "FDUSDUSDT", title: "FDUSD" },
          { proName: "DXY", title: "DXY" },
        ]}
        locale="vi_VN"
        displayMode="adaptive"
        largeChartUrl=""
        isTransparent={true}
        showSymbolLogo={true}
      />
      <div
        style={{
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          pointerEvents: "auto",
          cursor: "default",
        }}
        onClick={(e) => e.preventDefault()}
      />
      <div
        style={{
          position: "absolute",
          top: "4px",
          right: "10px",
          fontSize: "24px",
          zIndex: 1000,
          cursor: "default",
          display: "flex",
          alignItems: "center",
          gap: 8,
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <span>😊</span>
      </div>
    </div>
  );
});

const Screen = ({ order, setTab, isAdmin }: { order: number; setTab: Function; isAdmin: boolean }) => {
  switch (order) {
    case 0:
      return (
        <LayoutPrice title="Bảng USDT">
          <div className="flex flex-wrap gap-3 justify-center items-center mt-3 mx-5 mb-5">
            <PriceTable key="usdt-1" index={0} asset="USDT" />
            <PriceTable key="usdt-2" index={1} asset="USDT" />
            <PriceTable key="usdt-3" index={2} asset="USDT" />
            <PriceTable key="usdt-4" index={3} asset="USDT" />
            {isAdmin && (
              <PriceTable key="usdt-5" index={4} asset="USDT" />
            )}
          </div>
          <SliderCoin />
          <div>
            <div className="w-1/2 inline-block">
              <Chart
                candleMode="1"
                language="vi"
                pair="BINANCE:BTCUSDT"
                display="₿ Bitcoin"
              />
            </div>
            <div className="w-1/2 inline-block">
              <Chart
                candleMode="1"
                language="vi"
                pair="BINANCE:ETHUSDT"
                display="Ξ Ethereum"
              />
            </div>
            <div className="w-1/2 inline-block">
              <Chart
                candleMode="D"
                language="vi"
                pair="USDVND"
                display="USD (Bank)"
              />
            </div>
            <div className="w-1/2 inline-block">
              <Chart
                candleMode="D"
                language="vi"
                pair="DXY"
                display="Sức mạnh USD"
              />
            </div>
          </div>
          <AdvDetail initialAdvNo="12707426974119251968" />
        </LayoutPrice>
      );
    case 1:
      return (
        <LayoutPrice title="Bảng USDC">
          <div className="flex flex-wrap gap-3 justify-center items-center mt-3 mx-5 mb-5">
            <PriceTable key="usdc-1" index={0} asset="USDC" />
            <PriceTable key="usdc-2" index={1} asset="USDC" />
            <PriceTable key="usdc-3" index={2} asset="USDC" />
            <PriceTable key="usdc-4" index={3} asset="USDC" />
          </div>
          <Chart candleMode="1" language="vi" pair="BINANCE:USDCUSDT" />
        </LayoutPrice>
      );
    case 2:
      return (
        <LayoutPrice title="Bảng FDUSD">
          <div className="flex flex-wrap gap-3 justify-center items-center mt-3 mx-5 mb-5">
            <PriceTable key="fdusd-1" index={0} asset="FDUSD" />
            <PriceTable key="fdusd-2" index={1} asset="FDUSD" />
            <PriceTable key="fdusd-3" index={2} asset="FDUSD" />
            <PriceTable key="fdusd-4" index={3} asset="FDUSD" />
          </div>
          <Chart candleMode="1" language="vi" pair="BINANCE:FDUSDUSDT" />
        </LayoutPrice>
      );
    case 3:
      return (
        <LayoutPrice title="Bảng BTC">
          <div className="flex flex-wrap gap-3 justify-center items-center mt-3 mx-5 mb-5">
            <PriceTable key="btc-1" index={0} asset="BTC" />
            <PriceTable key="btc-2" index={1} asset="BTC" />
            <PriceTable key="btc-3" index={2} asset="BTC" />
            <PriceTable key="btc-4" index={3} asset="BTC" />
          </div>
          <Chart candleMode="1" language="vi" pair="BINANCE:BTCUSDT" />
        </LayoutPrice>
      );
    case 4:
      return (
        <LayoutPrice title="Bảng ETH">
          <div className="flex flex-wrap gap-3 justify-center items-center mt-3 mx-5 mb-5">
            <PriceTable key="eth-1" index={0} asset="ETH" />
            <PriceTable key="eth-2" index={1} asset="ETH" />
            <PriceTable key="eth-3" index={2} asset="ETH" />
            <PriceTable key="eth-4" index={3} asset="ETH" />
          </div>
          <Chart candleMode="1" language="vi" pair="BINANCE:ETHUSDT" />
        </LayoutPrice>
      );
    case 5:
      return (
        <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", height: "60vh", flexDirection: "column", gap: 2 }}>
          <Typography variant="h6" sx={{ color: "#94a3b8" }}>
            Nhấn nút QR dưới thanh toolbar để mở
          </Typography>
        </Box>
      );
    case 6:
      return (
        <LayoutPrice title="Bảng U">
          <div className="flex flex-wrap gap-3 justify-center items-center mt-3 mx-5 mb-5">
            <PriceTable key="u-1" index={0} asset="U" />
            <PriceTable key="u-2" index={1} asset="U" />
            <PriceTable key="u-3" index={2} asset="U" />
            <PriceTable key="u-4" index={3} asset="U" />
          </div>
        </LayoutPrice>
      );
    default:
      return <div />;
  }
};

function Main() {
  const [value, setValue] = React.useState(0);
  const [qrOpen, setQrOpen] = React.useState(false);
  const { role } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const isAdmin = role === 2;
  const isRouteAdmin = location.pathname === "/admin";

  return (
    <RefreshIntervalProvider>
      <div className="select-none min-h-screen" style={{ background: "#0f172a" }}>
        <div className="absolute inset-0 pointer-events-none overflow-hidden">
          <div
            style={{
              background: "radial-gradient(ellipse at 50% 0%, rgba(245,158,11,0.04) 0%, transparent 60%)",
            }}
          />
        </div>
        <div className="relative z-10">
          {isAdmin && (
            <Tooltip title="Quản trị">
              <IconButton
                onClick={() => navigate("/manage")}
                sx={{
                  position: "fixed",
                  top: "50%",
                  transform: "translateY(-50%)",
                  right: 8,
                  zIndex: 9999,
                  color: "#34d399",
                  backgroundColor: "rgba(52, 211, 153, 0.1)",
                  "&:hover": { backgroundColor: "rgba(52, 211, 153, 0.2)" },
                  display: { xs: "none", sm: "inline-flex" },
                }}
              >
                <AdminIcon />
              </IconButton>
            </Tooltip>
          )}
          <Screen order={value} setTab={setValue} isAdmin={isRouteAdmin} />
          <BottomBar value={value} setValue={setValue} onQrClick={() => setQrOpen(true)} onAdminClick={() => isAdmin && navigate("/manage")} />
          <QrModal open={qrOpen} onClose={() => setQrOpen(false)} />
        </div>
      </div>
    </RefreshIntervalProvider>
  );
}

export default Main;
